import React, { useEffect } from 'react'
import Img from 'gatsby-image'
import { useDispatch, useSelector } from 'react-redux'
import cn from 'classnames'
import { FormattedMessage, useIntl } from 'react-intl'
import _ from 'lodash'
import { Tracker } from '@gymondo/frontend-core/utils'
import { Button } from '@gymondo/frontend-core/components'
import { mediaXsOnly } from '@gymondo/frontend-core/style-vars'
import { useMediaQuery, useScript } from '@gymondo/frontend-core/hooks'

import { useGraphqlDataHook } from './use-graphql-data.hook'

import style from './community.module.scss'
import { config } from './config/community.config'
import { generateTrackingAttributes } from './util/tracking.util'
import { setConfig } from '../../../service/layout'
import { BackgroundImage, PageHero, ContentGrid } from '../../../component'

const YOUTUBE_PLAYER_ID = 'community_youtube_player'

const renderInstagramBottomSection = (instagramImages, intl) => (
    <div className={cn('row top-xs', style.instagramImages)}>
        <div className="col-xs-6 col-sm-4 col-sm-offset-2">
            <a
                href={intl.formatMessage({
                    id: 'community.link.instagram',
                })}
                target="_blank"
                rel="noopener noreferrer"
                {...generateTrackingAttributes({
                    label: 'instagram',
                    value: 'image-cta',
                })}
            >
                <Img fluid={instagramImages[2]} />
            </a>
        </div>
        <div className="col-xs-6 col-sm-5">
            <a
                href={intl.formatMessage({
                    id: 'community.link.instagram',
                })}
                target="_blank"
                rel="noopener noreferrer"
                {...generateTrackingAttributes({
                    label: 'instagram',
                    value: 'image-cta',
                })}
            >
                <Img fluid={instagramImages[1]} />
            </a>
        </div>
    </div>
)

const trackYoutube = () =>
    Tracker.push({
        event: Tracker.EVENTS.TRACKING,
        eventCategory: 'community',
        eventAction: 'community_yt_video_play',
    })

const onPlayerStateChange = (event) => {
    if (event.data === window.YT.PlayerState.PLAYING) {
        trackYoutube()
    }
}

const initPlayer = () => {
    // eslint-disable-next-line no-new
    new window.YT.Player(YOUTUBE_PLAYER_ID, {
        events: { onStateChange: onPlayerStateChange },
    })
}

const getCta = (id) => (
    <Button flex={false} size="medium">
        <FormattedMessage id={id} />
    </Button>
)

const getFacebookVisuals = (isXs, fbImage) => (
    <div className={cn(style.imageLinkWrapper, style.heroImageWrapper)}>
        {isXs ? (
            <Img fluid={fbImage} />
        ) : (
            <BackgroundImage
                fluid={fbImage}
                classParentName="image__background--center-position"
            />
        )}
    </div>
)

const getInstagramVisuals = (image) => (
    <div className={style.imageLinkWrapper}>
        <Img fluid={image} />
    </div>
)

const getYoutubeVisuals = (withCookie) => {
    const videoId = '9XKLxMyx8vc'

    return (
        <div className={cn(style.youtubeIframeWrapper, style.imageLinkWrapper)}>
            <iframe
                id={YOUTUBE_PLAYER_ID}
                width="550"
                height="310"
                title="youtube"
                src={
                    withCookie
                        ? `https://www.youtube.com/embed/${videoId}?enablejsapi=1`
                        : `https://www.youtube-nocookie.com/embed/${videoId}`
                }
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
            />
        </div>
    )
}

export const Community = () => {
    const { hasAnalyticsConsent } = useSelector((state) => state.cookieConsent)

    const [scriptLoaded, error] = useScript(
        hasAnalyticsConsent ? 'https://www.youtube.com/iframe_api' : ''
    )

    useEffect(() => {
        if (
            !error &&
            scriptLoaded &&
            window.YT &&
            typeof window.YT.Player === 'function'
        ) {
            initPlayer()
        } else {
            window.onYouTubeIframeAPIReady = initPlayer
        }
    }, [scriptLoaded, error])
    const intl = useIntl()
    const dispatch = useDispatch()
    const data = useGraphqlDataHook()
    useEffect(() => {
        dispatch(setConfig({ textColorVariant: 'white', logoVariant: 'white' }))
    }, [dispatch])
    const isXs = useMediaQuery(mediaXsOnly)

    const fbImage = _.get(data, 'facebook.edges.0.node.childImageSharp.fluid')
    const instagramImages = _.get(data, 'instagram.edges').map((edge) =>
        _.get(edge, 'node.childImageSharp.fluid')
    )

    const lang = intl.locale.split('-')[0]

    const localizedInstagramImages = data?.instagramLocalized?.edges?.filter(
        (edge) => edge.node.childImageSharp.fluid.originalName.includes(lang)
    )

    instagramImages.push(
        localizedInstagramImages[0]?.node.childImageSharp.fluid
    )

    const { hero, facebook, instagram, youtube } = config

    return (
        <div className={style.page}>
            <PageHero
                title={hero.title}
                subtitle={hero.subtitle}
                className={style.heroWrapper}
            />
            <div className={cn('wrapper', style.wrapper)}>
                <ContentGrid
                    config={facebook}
                    descriptionChildren={getCta(facebook.cta)}
                    visualChildren={getFacebookVisuals(isXs, fbImage)}
                />
                <ContentGrid
                    config={instagram}
                    descriptionChildren={getCta(instagram.cta)}
                    visualChildren={getInstagramVisuals(instagramImages[0])}
                />
                {renderInstagramBottomSection(instagramImages, intl, isXs)}
                <ContentGrid
                    config={youtube}
                    descriptionChildren={getCta(youtube.cta)}
                    visualChildren={getYoutubeVisuals(hasAnalyticsConsent)}
                />
            </div>
        </div>
    )
}

export default Community
